// Joana chat shell — Phoenix REST client (:7419, /api/v1). A thin, typed wrapper
// over fetch for the auth + chat surfaces the shell needs. It holds the bearer
// access token (set after login), attaches it to authenticated calls, and
// normalizes errors into a single ApiError so the store has one failure shape.
//
// The shell is TRUSTED first-party code, so it talks to the API directly (no
// capability broker / isolate — that boundary is only for untrusted mini-apps).

import { hostTranslator } from "../i18n/index.ts";
import {
  BUNDLE_FORMAT,
  SUPPORTED_BUNDLE_VERSION,
  type Channel,
  type ChannelListing,
  type ChannelPost,
  type ReactionCount,
  type Chat,
  type ActivityEvent,
  type CirclePost,
  type LoginResult,
  isTwoFactor,
  type Message,
  type MiniAppBundleEnvelope,
  type MiniAppListItem,
  type MiniAppPackage,
  type PaymentChargeResult,
  type PostComment,
  type PrivacyAudience,
  type AssistantSetup,
  type OrgAssistant,
  type OrgApiKey,
  type Organisation,
  type OrganisationMember,
  type Publisher,
  type WirePerson,
  type Zone,
  type StatusItem,
  type StatusTrayEntry,
  type StatusViewerRow,
  type User,
  type WebLoginCreated,
  type E2eDeviceRegistration,
  type E2eDeviceSummary,
  type PeerDeviceRef,
  type WebLoginPollResult,
} from "./types.ts";
import { currentPublisherKeyHex, verifyBundleSignature } from "./miniapp-trust.ts";
import type { ChatRosterMember } from "./author-names.ts";

/** Injected so tests can drive the client without a network (fetch-compatible). */
export type FetchLike = (
  url: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
  },
) => Promise<{
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
  text: () => Promise<string>;
  /** Response headers (real fetch provides this; fakes may omit it — an
   *  absent header then reads as null, which fails closed where it must). */
  headers?: { get(name: string): string | null };
}>;

/** What came of trying to rotate the session — see `ApiClient.rotate`. */
type RotateOutcome = "refreshed" | "rejected" | "unavailable";

export class ApiError extends Error {
  readonly status: number;
  readonly body?: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = "ApiError";
  }
}

/**
 * ADR-0037 B — a zone whose minimum web build is above this bundle answered
 * `403` + `code: "zone_update_required"`. Deliberately not 426, which is the
 * whole-app wall. Matched on the CODE, never on the prose beside it.
 */
export function isZoneUpdateRequired(e: unknown): boolean {
  return (
    e instanceof ApiError &&
    e.status === 403 &&
    typeof e.body === "object" &&
    e.body !== null &&
    (e.body as { code?: unknown }).code === "zone_update_required"
  );
}

export interface ApiClientOptions {
  baseUrl: string;
  fetch?: FetchLike;
  /** Seed an existing token (e.g. restored from storage). */
  token?: string | null;
  /** Override the store's bundle-signing public key (tests only). */
  publisherKeyHex?: string;
  /** ADR-0033 D9 — this bundle's identity for the server's version floor.
   *  Omitted means unidentified, which is the server's `unknown`. */
  clientBuild?: { platform: string; build: number };
}

/** The three keys a timeline post carries, as the reaction routes reply. */
export interface ReactionWire {
  reactions: ReactionCount[];
  reaction_total: number;
  my_reaction: string | null;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly doFetch: FetchLike;
  private token: string | null;

  /**
   * ADR-0033 D9 — which build of which client this is, sent on every request
   * as `X-Client-Platform` / `X-Client-Build`. The server refuses anything
   * below its per-platform floor with `426`.
   *
   * On the web the "build" is the deployed bundle, and the failure this closes
   * is a page left open for days, or a browser serving a cached bundle after a
   * deploy — not an app somebody forgot to update. The remedy is a reload, and
   * that is what the wall offers.
   *
   * Unset means unidentified, which is what the server calls `unknown` and
   * what a test or a tool should look like.
   */
  private readonly clientBuild: { platform: string; build: number } | null;

  /** Invoked the first time the server answers `426`. The shell raises a latch
   *  from it and draws a full-screen wall; it is never called for anything
   *  else, and never for a 401. */
  onUpdateRequired: (() => void) | null = null;

  /**
   * The rotating refresh token, held here because this is the layer that
   * notices a 401 and the only one that can do anything about it.
   *
   * It used to be written to `localStorage` on sign-in and never read again by
   * anything — so the browser kept, for a year, the one credential that could
   * have saved the session, and signed people out anyway the moment the access
   * token aged out. A tab left open overnight, or simply reloaded, was a
   * sign-in screen.
   */
  private refreshTokenValue: string | null = null;

  /** One rotation at a time: a page that fires six requests on load must not
   *  fire six refreshes, five of which then replay an already-rotated token. */
  private rotating: Promise<RotateOutcome> | null = null;

  /** A rotation succeeded — persist the new pair (both rotate, every time). */
  onTokensRefreshed: ((accessToken: string, refreshToken: string) => void) | null = null;

  /** The session is definitively over: the server refused the refresh token
   *  itself. NEVER fired for a failure we could not interpret. */
  onSessionEnded: (() => void) | null = null;

  setRefreshToken(token: string | null): void {
    this.refreshTokenValue = token;
  }

  /**
   * Exchange the refresh token for a fresh pair, at most once concurrently.
   *
   * Three outcomes, not two, for the reason the shared mobile client carries
   * the same split: `"rejected"` is a verdict on the session and costs the
   * person their sign-in, while `"unavailable"` means we never got an answer —
   * offline, a `502` from the proxy, a `503` while the API restarts. Treating
   * the second as the first is how a passing hiccup became a permanent
   * sign-out.
   */
  private rotate(sentWith: string | null): Promise<RotateOutcome> {
    // Somebody else already rotated while this request was in flight.
    if (this.token && this.token !== sentWith) return Promise.resolve<RotateOutcome>("refreshed");
    if (!this.rotating) {
      const p = this.doRotate();
      this.rotating = p;
      const clear = () => {
        if (this.rotating === p) this.rotating = null;
      };
      void p.then(clear, clear);
    }
    return this.rotating;
  }

  private async doRotate(): Promise<RotateOutcome> {
    const rt = this.refreshTokenValue;
    // Nothing to rotate WITH — no amount of waiting fixes that.
    if (!rt) return "rejected";
    let res: LoginResult;
    try {
      res = await this.refresh(rt);
    } catch (e) {
      if (e instanceof ApiError) {
        // The build is refused; the wall is already up. Let it through rather
        // than dressing it as a session problem.
        if (e.status === 426) throw e;
        // The ONE status that means this credential is dead. The server keeps
        // it for exactly that and answers 503 when the fault is its own.
        if (e.status === 401) return "rejected";
      }
      return "unavailable";
    }
    // `/auth/refresh` cannot answer with a 2FA challenge (2FA was proved at
    // sign-in). If it somehow does, the server is broken — which is a reason to
    // try later, not to sign somebody out.
    if (isTwoFactor(res)) return "unavailable";
    this.token = res.access_token;
    this.refreshTokenValue = res.refresh_token;
    this.onTokensRefreshed?.(res.access_token, res.refresh_token);
    return "refreshed";
  }

  /** The current access token — read by the avatar loader, whose fetches an
   *  `<img>` cannot make (no header on an img request). Never put in a URL. */
  get bearerToken(): string | null {
    return this.token;
  }
  private readonly publisherKeyHex: string;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    // Bind the global fetch to its realm: an unbound `window.fetch` throws
    // "Illegal invocation" in the browser. Tests inject their own fetch.
    const globalFetch = (globalThis as { fetch?: FetchLike }).fetch;
    this.doFetch = opts.fetch ?? (globalFetch ? (globalFetch.bind(globalThis) as FetchLike) : undefined as unknown as FetchLike);
    this.token = opts.token ?? null;
    this.clientBuild = opts.clientBuild ?? null;
    this.publisherKeyHex = opts.publisherKeyHex ?? currentPublisherKeyHex();
    if (!this.doFetch) throw new Error("ApiClient: no fetch implementation available");
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  // --- auth ----------------------------------------------------------------

  /** POST /auth/login — single-factor returns tokens; 2FA returns a challenge. */
  login(username: string, password: string): Promise<LoginResult> {
    return this.request<LoginResult>("POST", "/auth/login", {
      body: { username, password },
      auth: false,
    });
  }

  /** POST /auth/2fa/verify — completes a 2FA challenge into a session. */
  verifyTwoFactor(challengeToken: string, code: string): Promise<LoginResult> {
    return this.request<LoginResult>("POST", "/auth/2fa/verify", {
      body: { challenge_token: challengeToken, code },
      auth: false,
    });
  }

  /** POST /auth/send-otp — request a one-time code (phone login / verification). */
  sendOtp(phone: string): Promise<{ ok?: boolean; expires_in?: number }> {
    return this.request("POST", "/auth/send-otp", { body: { phone }, auth: false });
  }

  /** POST /auth/verify-otp — exchange a code for a session. */
  verifyOtp(phone: string, code: string): Promise<LoginResult> {
    return this.request<LoginResult>("POST", "/auth/verify-otp", {
      body: { phone, code },
      auth: false,
    });
  }

  /**
   * POST /auth/web-login — open a QR login session. `client_secret` is
   * returned exactly once and must never leave this browser tab: token
   * delivery at poll time is bound to it (anti-QRLjacking).
   */
  webLoginCreate(signal?: { identityKey: string; registrationId: number }): Promise<WebLoginCreated> {
    // The identity goes in the CREATE, and the server puts it in the QR — which
    // is the channel it is not in. Sending it here as well is not a duplication
    // to trim: it is what lets the server refuse an approval whose key does not
    // match the tab that asked (ADR-0027 D4.4).
    const body = signal
      ? { identity_key: signal.identityKey, registration_id: signal.registrationId }
      : {};
    return this.request<WebLoginCreated>("POST", "/auth/web-login", { body, auth: false });
  }

  /** POST /e2e/devices — publish this browser as a Signal device. Requires the
   *  primary's `link_signature`: an unvouched device is refused by the server
   *  and, more importantly, by every peer (ADR-0027 D4.3). */
  e2eRegisterDevice(body: E2eDeviceRegistration): Promise<E2eDeviceSummary> {
    return this.request<E2eDeviceSummary>("POST", "/e2e/devices", { body });
  }

  /** POST /e2e/devices/:id/prekeys — top up the one-time prekeys peers consume
   *  when they open a session with this browser. */
  e2eUploadPrekeys(deviceRowId: string, prekeys: Array<{ key_id: number; public_key: string }>): Promise<unknown> {
    return this.request("POST", `/e2e/devices/${encodeURIComponent(deviceRowId)}/prekeys`, {
      body: { prekeys: prekeys.map((p) => ({ ...p, key_kind: "one_time" })) },
    });
  }

  /** POST /auth/web-login/poll — the browser-side status heartbeat. On
   *  `approved` the standard LoginResponse rides under `login` (delivered
   *  exactly once — a replay polls as `expired`). */
  webLoginPoll(sid: string, clientSecret: string): Promise<WebLoginPollResult> {
    return this.request<WebLoginPollResult>("POST", "/auth/web-login/poll", {
      body: { sid, client_secret: clientSecret },
      auth: false,
    });
  }

  /** POST /auth/refresh — rotate the refresh token into a fresh session. */
  refresh(refreshToken: string): Promise<LoginResult> {
    return this.request<LoginResult>("POST", "/auth/refresh", {
      body: { refresh_token: refreshToken },
      auth: false,
    });
  }

  // NOTE: `profile()` and `searchUsers()` were each declared TWICE in this
  // class. A later declaration silently replaces an earlier one, so the first
  // pair never ran — and they disagreed with the pair that did about the shape
  // of the answer (`User` vs `{ user: User }`), which is what made
  // `shell.ts` read `.user` off a value typed as a bare `User`. The live
  // definitions are the later ones, near `request`; these are removed rather
  // than reconciled, because the ones that were running are the ones the
  // server actually matches.

  /** POST /auth/logout — revoke this session's family server-side. */
  logout(refreshToken?: string): Promise<{ ok: boolean }> {
    return this.request("POST", "/auth/logout", {
      body: refreshToken ? { refresh_token: refreshToken } : {},
    });
  }

  // --- chats + messages ----------------------------------------------------

  /** GET /chats — most-recent-active-first, with unread_count + last_message. */
  listChats(): Promise<Chat[]> {
    return this.request<Chat[]>("GET", "/chats");
  }

  /** POST /chats/:id/read — clear unread for a chat. */
  markChatRead(chatId: string): Promise<unknown> {
    return this.request("POST", `/chats/${encodeURIComponent(chatId)}/read`, { body: {} });
  }

  /** GET /chats/:id/members — the chat's FULL roster, viewer included. */
  chatRoster(chatId: string): Promise<{ members: ChatRosterMember[] }> {
    return this.request("GET", `/chats/${encodeURIComponent(chatId)}/members`);
  }

  /** GET /messages?chat_id=…&limit=… — most-recent-first page. */
  listMessages(
    chatId: string,
    opts: { limit?: number; before?: string } = {},
  ): Promise<Message[]> {
    const q = new URLSearchParams({ chat_id: chatId });
    if (opts.limit) q.set("limit", String(opts.limit));
    if (opts.before) q.set("before", opts.before);
    return this.request<Message[]>("GET", `/messages?${q.toString()}`);
  }

  /** POST /messages — persist a message (REST path; realtime is the live path). */
  sendMessage(input: {
    chat_id: string;
    body: string;
    message_type?: string;
    client_msg_id?: string;
  }): Promise<Message> {
    return this.request<Message>("POST", "/messages", { body: input });
  }

  // --- mini-app registry ---------------------------------------------------

  /** GET /miniapps/featured — top published apps, mapped to picker rows. */
  async listFeaturedMiniApps(limit = 20): Promise<MiniAppListItem[]> {
    const apps = await this.request<Array<Record<string, unknown>>>(
      "GET",
      `/miniapps/featured?limit=${limit}`,
    );
    return (apps ?? []).map(toListItem);
  }

  /** GET /miniapps/installed — the user's installed apps, mapped to picker rows. */
  async listInstalledMiniApps(): Promise<MiniAppListItem[]> {
    const res = await this.request<{ installations?: Array<Record<string, unknown>> }>(
      "GET",
      "/miniapps/installed",
    );
    return (res.installations ?? []).map((row) => toListItem((row.miniapp ?? row) as Record<string, unknown>));
  }

  /** GET /miniapps/search?q=… — registry search, mapped to picker rows. The
   *  endpoint may return a bare array or `{results:[…]}`; both are accepted. */
  async searchMiniApps(query: string, limit = 20): Promise<MiniAppListItem[]> {
    const q = new URLSearchParams({ q: query, limit: String(limit) });
    const res = await this.request<unknown>("GET", `/miniapps/search?${q.toString()}`);
    const rows = Array.isArray(res)
      ? res
      : ((res as { results?: unknown[] })?.results ?? []);
    return (rows as Array<Record<string, unknown>>).map(toListItem);
  }

  /**
   * GET /miniapps/:id/bundle — fetch a compiled bundle, **verify the store's
   * Ed25519 signature (`x-sema-package-signature`) over the exact body bytes
   * against the baked-in publisher key BEFORE parsing**, then validate the
   * envelope (`sema-miniapp-bundle`, miniapps/dsl/bundle.schema.json) and
   * reduce it to the `{manifest, code}` the host launches. Unsigned, tampered,
   * or wrong-key bundles throw and are never evaluated — the same fail-closed
   * gate as the mobile `MiniAppBundleLoader`. The isolate evaluates `logic` as
   * a global script; `views`/`handlers` ride along for the SDUI render path.
   */
  async fetchMiniAppPackage(id: string): Promise<MiniAppPackage> {
    const path = `/miniapps/${encodeURIComponent(id)}/bundle`;
    if (!this.token) throw new ApiError(401, "not authenticated");

    let res: Awaited<ReturnType<FetchLike>>;
    try {
      res = await this.doFetch(`${this.baseUrl}/api/v1${path}`, {
        method: "GET",
        headers: { authorization: `Bearer ${this.token}` },
      });
    } catch (e) {
      throw new ApiError(0, `network error: ${(e as Error).message}`);
    }
    if (!res.ok) {
      throw new ApiError(res.status, `request failed (${res.status})`);
    }

    // The signature was taken over the exact stored bytes; the body is the
    // exact stored bytes (UTF-8 JSON), so re-encoding the text is lossless.
    const text = await res.text();
    const signature = res.headers?.get("x-sema-package-signature") ?? null;
    try {
      await verifyBundleSignature(new TextEncoder().encode(text), signature, this.publisherKeyHex);
    } catch (e) {
      throw new ApiError(0, (e as Error).message);
    }

    let env: MiniAppBundleEnvelope;
    try {
      env = JSON.parse(text) as MiniAppBundleEnvelope;
    } catch {
      throw new ApiError(0, "bundle is not valid JSON");
    }
    if (env.format !== BUNDLE_FORMAT) {
      throw new ApiError(0, `unexpected bundle format: ${env.format}`);
    }
    if (env.bundle_version > SUPPORTED_BUNDLE_VERSION) {
      throw new ApiError(0, `bundle version ${env.bundle_version} is newer than supported (${SUPPORTED_BUNDLE_VERSION})`);
    }
    if (typeof env.logic !== "string" || env.logic.length === 0) {
      throw new ApiError(0, "bundle has no logic");
    }
    return { manifest: env.manifest, code: env.logic };
  }

  // --- realtime ------------------------------------------------------------

  /**
   * POST /api/v1/realtime/ticket — mint a short-lived single-use WS ticket. The
   * browser can't set the `Authorization` header on a WebSocket handshake, so we
   * exchange the session (Bearer header on THIS call) for a ticket and connect the
   * socket with `?ticket=…`. A fresh ticket is fetched for every (re)connect — it's
   * single-use, so a reconnect can't replay the previous one.
   */
  realtimeTicket(): Promise<{ ticket: string; expires_in: number }> {
    return this.request("POST", "/realtime/ticket", { body: {} });
  }

  // --- payments ------------------------------------------------------------

  /**
   * POST /api/v1/payments/initiate — charge through Phoenix → the Rust payments
   * service (ADR-0002). Called by the TRUSTED payment sheet AFTER the user
   * approves a mini-app's `payments.intent`; the mini-app never reaches this.
   */
  initiatePayment(input: {
    amount: number;
    currency: string;
    provider: string;
    phone_number: string;
    description?: string;
    idempotency_key?: string;
  }): Promise<PaymentChargeResult> {
    return this.request<PaymentChargeResult>("POST", "/payments/initiate", { body: input });
  }

  // --- core ----------------------------------------------------------------

  // --- Avatars (ADR-0022) --------------------------------------------------

  /** Upload both renditions (already cropped square 640/192 JPEG blobs) and
   *  answer the VERSION to bind. Multipart, so it bypasses request(). */
  async uploadAvatar(full: Blob, thumb: Blob): Promise<{ version: string }> {
    if (!this.token) throw new ApiError(401, "not authenticated");
    const form = new FormData();
    form.append("full", full, "full.jpg");
    form.append("thumb", thumb, "thumb.jpg");
    let res: Awaited<ReturnType<FetchLike>>;
    try {
      res = await this.doFetch(`${this.baseUrl}/api/v1/avatars`, {
        method: "POST",
        headers: { authorization: `Bearer ${this.token}` },
        body: form as unknown as string,
      });
    } catch (e) {
      throw new ApiError(0, `network error: ${(e as Error).message}`);
    }
    const payload = await safeJson(res);
    if (!res.ok) throw new ApiError(res.status, "upload failed", payload);
    return payload as { version: string };
  }

  /** PATCH /auth/profile — display name, avatar (null clears), frame. An
   *  ABSENT key leaves the field alone; an explicit null clears it. */
  updateProfile(input: Record<string, unknown>): Promise<{ user: User }> {
    return this.request<{ user: User }>("PATCH", "/auth/profile", { body: input });
  }

  avatarPrivacy(): Promise<{ policy: string; except: { user_id: string; username: string | null; display_name: string | null }[] }> {
    return this.request("GET", "/auth/profile/avatar-privacy");
  }

  setAvatarPrivacy(policy: string, except: string[]): Promise<{ policy: string; except: { user_id: string; username: string | null; display_name: string | null }[] }> {
    return this.request("PUT", "/auth/profile/avatar-privacy", { body: { policy, except } });
  }

  // --- Status (D8, ADR-0023) -----------------------------------------------
  //
  // Posting is NOT here: a post must reach the audience, which only realtime
  // can do, so it goes through the socket (`post_status`). Everything below
  // reaches nobody but the caller, which is why it is plain REST.

  statusTray(): Promise<{ tray: StatusTrayEntry[] }> {
    return this.request("GET", "/statuses/tray");
  }

  ownStatuses(): Promise<{ statuses: StatusItem[] }> {
    return this.request("GET", "/statuses/own");
  }

  statusArchive(): Promise<{ statuses: StatusItem[] }> {
    return this.request("GET", "/statuses/archive");
  }

  setStatusArchive(enabled: boolean): Promise<{ enabled: boolean }> {
    return this.request("PUT", "/statuses/archive", { body: { enabled } });
  }

  // --- Circle posts (D9, ADR-0024) ------------------------------------
  //
  // Reads plus the comment write. PUBLISHING goes through the socket
  // (`post_circle`) for the reason posting a status does: it has to reach an
  // audience and the API cannot push to a connected client. There is no
  // `likePost` or `deletePost` here — a post IS a status row, so `likeStatus`
  // and `deleteStatus` already act on one, and two routes to one behaviour is
  // how the two come to drift.

  postFeed(before?: string): Promise<CirclePost[]> {
    const q = before ? `?before=${encodeURIComponent(before)}` : "";
    return this.request("GET", `/posts${q}`);
  }

  postComments(postId: string): Promise<{ comments: PostComment[] }> {
    return this.request("GET", `/posts/${encodeURIComponent(postId)}/comments`);
  }

  createPostComment(postId: string, body: string): Promise<{ comment: PostComment }> {
    return this.request("POST", `/posts/${encodeURIComponent(postId)}/comments`, { body: { body } });
  }

  deletePostComment(commentId: string): Promise<unknown> {
    return this.request("DELETE", `/posts/comments/${encodeURIComponent(commentId)}`);
  }

  // --- Channels (D6, ADR-0024) ----------------------------------------
  //
  // Reads and a follow. PUBLISHING is REST too (unlike a circle post) and is
  // not here: this shell reads channels, it does not run one — an Office is a
  // separate surface with a separate audience.

  /** Channels this viewer follows, newest activity first. */
  channels(): Promise<{ channels: Channel[] }> {
    return this.request("GET", "/channels");
  }

  /** "Find channels to follow" — publishers this viewer does not follow. */
  channelSuggestions(): Promise<{ channels: Channel[] }> {
    return this.request("GET", "/channels/suggestions");
  }

  /**
   * Explore — the channel directory (ADR-0024 A16): browse by `kind`, search
   * by `q`.
   *
   * An unrecognised `kind` answers 422 rather than filtering to nothing, so a
   * client that has drifted from the taxonomy learns it from the response.
   */
  channelDirectory(opts: {
    kind?: string | null;
    q?: string | null;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ channels: ChannelListing[] }> {
    const params = new URLSearchParams();
    if (opts.kind) params.set("kind", opts.kind);
    if (opts.q) params.set("q", opts.q);
    if (opts.limit != null) params.set("limit", String(opts.limit));
    if (opts.offset != null) params.set("offset", String(opts.offset));
    const query = params.toString();
    // `this.request`, not `this.get`. There is no `get` method on this class
    // and never has been, so every call to Explore's browse and search on web
    // threw `this.get is not a function` from the day it shipped — the screen
    // renders, the facets render, and the list is permanently the error state.
    //
    // FOURTH name-that-does-not-exist to reach this shell (`this.update`,
    // `extra`, `text-input`, now this). TypeScript would have caught every one
    // of them and nothing was running it: `--experimental-transform-types`
    // STRIPS types, it does not check them. `npm run typecheck` does, and it is
    // in the gate now.
    return this.request("GET", `/channels/explore${query ? `?${query}` : ""}`);
  }

  /**
   * The publisher kinds, as the SERVER defines them.
   *
   * Fetched, never hardcoded: the taxonomy went from nine values to seven in
   * one migration (058 → 059), and a client copy is exactly how it drifts from
   * the CHECK that enforces it.
   */
  publisherKinds(): Promise<{ kinds: string[] }> {
    return this.request("GET", "/channels/kinds");
  }

  /**
   * Is this chat end-to-end encrypted? (ADR-0007 — the PRESENCE of a row.)
   *
   * Asked per chat and never cached across chats: the flag is a property of
   * the conversation, it can be turned on from a phone while a browser is
   * looking at the list, and a stale "no" here is a screen telling somebody
   * their messages are readable when they are not.
   */
  chatE2EState(chatId: string): Promise<{ chat_id: string; e2e: boolean; epoch?: number }> {
    return this.request("GET", `/e2e/chats/${encodeURIComponent(chatId)}/state`);
  }

  /**
   * A peer's ACTIVE Signal devices — identifiers only, no prekey consumed.
   *
   * The safety-number screen's one lookup. A fingerprint is computed against a
   * peer DEVICE, not against a person, so the screen has to know which device
   * numbers exist before it can ask the worker for anything. `device_id` on
   * the wire is the Signal device NUMBER; the `id` beside it is the server's
   * row uuid and is not what the protocol addresses.
   *
   * Explicitly NOT the bundle fetch (`/e2e/keys/:user_id`), which consumes a
   * one-time prekey. Opening a screen must not cost the peer forward secrecy.
   */
  peerDevices(userId: string): Promise<{ devices: PeerDeviceRef[] }> {
    return this.request("GET", `/e2e/peers/${encodeURIComponent(userId)}/devices`);
  }

  /**
   * The channel with a page of its posts, newest first (ADR-0036). `before`
   * is the oldest post id already held; the server pages from there.
   */
  channelTimeline(channelId: string, before?: string | null): Promise<{ channel?: Channel | null; posts: ChannelPost[] }> {
    const q = before ? `?before=${encodeURIComponent(before)}` : "";
    return this.request("GET", `/channels/${encodeURIComponent(channelId)}${q}`);
  }

  /** ONE emoji per reader per post; the reply is the post's new aggregate,
   *  and `null` takes this reader's reaction off. */
  reactToChannelPost(channelId: string, postId: string, emoji: string | null): Promise<ReactionWire> {
    const path = `/channels/${encodeURIComponent(channelId)}/posts/${encodeURIComponent(postId)}/reaction`;
    return emoji === null
      ? this.request("DELETE", path)
      : this.request("PUT", path, { body: { emoji } });
  }

  /** Mute = "stop waking me", never "hide it": the row stays, the badge does
   *  not. */
  setChannelMuted(channelId: string, muted: boolean): Promise<unknown> {
    return this.request("PUT", `/channels/${encodeURIComponent(channelId)}/mute`, { body: { muted } });
  }

  markChannelRead(channelId: string): Promise<unknown> {
    return this.request("POST", `/channels/${encodeURIComponent(channelId)}/read`);
  }

  /** Follow a publisher. The channel list and the suggestions are two halves
   *  of one answer, so the caller re-reads both rather than moving a row. */
  followChannel(channelId: string): Promise<unknown> {
    return this.request("POST", `/business/${encodeURIComponent(channelId)}/follow`);
  }

  // --- Mazungumzo (D12, ADR-0025) ------------------------------------------
  //
  // Neither response carries a score. The server ranks and hands back rows in
  // an order; a score a client can read is a number it can sort by, cache or
  // show, and the first time it is shown it is a popularity number wearing a
  // different name (D8).

  /** "What's being said" — ranked on recency, city, language and proximity. */
  mazungumzo(limit?: number): Promise<{ posts: CirclePost[] }> {
    const q = limit != null ? `?limit=${limit}` : "";
    return this.request("GET", `/posts/mazungumzo${q}`);
  }

  /** "Following" — chronological. Empty is a real answer and the shell says so
   *  rather than showing the ranked feed instead. */
  mazungumzoFollowing(limit?: number): Promise<{ posts: CirclePost[] }> {
    const q = limit != null ? `?limit=${limit}` : "";
    return this.request("GET", `/posts/mazungumzo/following${q}`);
  }

  // --- ADR-0029: Threads inside WaveTalk --------------------------------
  //
  // Repost and save are POST/DELETE rather than a toggle: a request whose
  // reply never arrives is ordinary on these connections, and a toggle cannot
  // be retried without flipping the state back.

  repost(postId: string): Promise<{ reposted: boolean }> {
    return this.request("POST", `/posts/${encodeURIComponent(postId)}/repost`);
  }

  unrepost(postId: string): Promise<{ reposted: boolean }> {
    return this.request("DELETE", `/posts/${encodeURIComponent(postId)}/repost`);
  }

  savePost(postId: string): Promise<{ saved: boolean }> {
    return this.request("POST", `/posts/${encodeURIComponent(postId)}/save`);
  }

  unsavePost(postId: string): Promise<{ saved: boolean }> {
    return this.request("DELETE", `/posts/${encodeURIComponent(postId)}/save`);
  }

  savedPosts(): Promise<{ posts: CirclePost[] }> {
    return this.request("GET", "/posts/saved");
  }

  likedPosts(): Promise<{ posts: CirclePost[] }> {
    return this.request("GET", "/posts/liked");
  }

  postThread(postId: string): Promise<{ posts: CirclePost[] }> {
    return this.request("GET", `/posts/${encodeURIComponent(postId)}/thread`);
  }

  /** `filter` is "mentions", "following", or omitted for everything. */
  activity(filter?: string): Promise<{ activity: ActivityEvent[] }> {
    const q = filter ? `?filter=${encodeURIComponent(filter)}` : "";
    return this.request("GET", `/posts/activity${q}`);
  }

  /** Public posts and people only — the server enforces it, and no `scope`
   *  parameter exists here on purpose. */
  searchPosts(query: string): Promise<{ posts: CirclePost[]; people: WirePerson[] }> {
    return this.request("GET", `/posts/search?q=${encodeURIComponent(query)}`);
  }

  /** Who this viewer follows. There is deliberately no inverse and no count,
   *  for anybody, including themselves (ADR-0025 D2). */
  following(): Promise<{ users: WirePerson[] }> {
    return this.request("GET", "/follows");
  }

  followPerson(userId: string): Promise<{ user_id: string; following: boolean }> {
    return this.request("POST", `/follows/${encodeURIComponent(userId)}`);
  }

  unfollowPerson(userId: string): Promise<{ user_id: string; following: boolean }> {
    return this.request("DELETE", `/follows/${encodeURIComponent(userId)}`);
  }

  /** The cities a person may declare (ADR-0024 A14). Served, never a copy. */
  cities(): Promise<{ cities: string[] }> {
    return this.request("GET", "/geo/cities");
  }

  // --- Office — the publishers I own (ADR-0024 D6/D7, Phase 3) -------------

  /** Office's list. NOT `/business`, which is the public directory: this is
   *  "what do I run?" for one person, unpaged and ordered by name. */
  myPublishers(): Promise<{ publishers: Publisher[] }> {
    return this.request("GET", "/business/mine");
  }

  /**
   * Create a publisher.
   *
   * `account_type` is deliberately not a field and must never become one — see
   * `Publisher.account_type`.
   */
  createPublisher(body: {
    name: string;
    display_name: string;
    kind: string;
    email: string;
    phone: string;
    country: string;
    description?: string | null;
    category?: string | null;
  }): Promise<Publisher> {
    return this.request("POST", "/business", { body });
  }

  /** Turn publishing on or off. Owner-set; an operator revokes through the
   *  same column, which is the lever that stops the NEXT post. */
  setPublishing(publisherId: string, publishes: boolean): Promise<{ id: string; publishes: boolean }> {
    return this.request(
      "PUT",
      `/channels/${encodeURIComponent(publisherId)}/publishing`,
      { body: { publishes } }
    );
  }

  // --- Office — organisations and zones (ADR-0018 step 1) ------------------
  //
  // Reads, plus the two mutations that reach nobody else's screen. The four
  // that DO — adding a person, removing one, changing a role, creating a zone
  // — go over the SOCKET, because the API cannot push to a connected client
  // and a REST twin here would be a second path that skipped the doorbell.

  organisations(): Promise<{ organisations: Organisation[] }> {
    return this.request("GET", "/office/organisations");
  }

  organisation(orgId: string): Promise<Organisation> {
    return this.request("GET", `/office/organisations/${encodeURIComponent(orgId)}`);
  }

  /** Create. REST because it reaches nobody — the creator is its only member. */
  createOrganisation(body: { name: string; slug?: string | null }): Promise<Organisation> {
    return this.request("POST", "/office/organisations", { body });
  }

  organisationMembers(orgId: string): Promise<{ members: OrganisationMember[] }> {
    return this.request("GET", `/office/organisations/${encodeURIComponent(orgId)}/members`);
  }

  /** The zones of an organisation this viewer holds a seat in — chat rows,
   *  because a zone IS a chat and its list row is the same row. */
  organisationZones(orgId: string): Promise<{ zones: Chat[] }> {
    return this.request("GET", `/office/organisations/${encodeURIComponent(orgId)}/zones`);
  }

  zone(chatId: string): Promise<{ zone: Zone; organisation: Organisation; chat: Chat }> {
    return this.request("GET", `/office/zones/${encodeURIComponent(chatId)}`);
  }

  // --- Office — the assistant and API keys (ADR-0037 E) --------------------
  //
  // REST, because none of these reaches anybody else's screen. Switching the
  // assistant on in a zone does — a seat is a roster change — so that one is
  // the socket's `set_zone_assistant`, in the shell.

  assistantSetup(orgId: string): Promise<AssistantSetup> {
    return this.request("GET", `/office/organisations/${encodeURIComponent(orgId)}/assistant`);
  }

  createAssistant(orgId: string, displayName: string): Promise<{ assistant: OrgAssistant }> {
    return this.request("POST", `/office/organisations/${encodeURIComponent(orgId)}/assistant`, {
      body: { display_name: displayName },
    });
  }

  renameAssistant(orgId: string, displayName: string): Promise<{ assistant: OrgAssistant }> {
    return this.request("PATCH", `/office/organisations/${encodeURIComponent(orgId)}/assistant`, {
      body: { display_name: displayName },
    });
  }

  orgApiKeys(orgId: string): Promise<{ api_keys: OrgApiKey[] }> {
    return this.request("GET", `/office/organisations/${encodeURIComponent(orgId)}/api-keys`);
  }

  /** The answer's `key` is the secret — the one moment it exists in this tab. */
  issueOrgApiKey(
    orgId: string,
    body: { name: string; scopes: string[]; expires_in_days: number },
  ): Promise<{ key: string; api_key: OrgApiKey }> {
    return this.request("POST", `/office/organisations/${encodeURIComponent(orgId)}/api-keys`, { body });
  }

  revokeOrgApiKey(orgId: string, keyId: string): Promise<{ api_key: OrgApiKey }> {
    return this.request(
      "DELETE",
      `/office/organisations/${encodeURIComponent(orgId)}/api-keys/${encodeURIComponent(keyId)}`,
    );
  }

  /** Leave. REST for the same reason `leave_chat` has no socket twin: the only
   *  screen that changes is this one. The owner may not — the server refuses. */
  leaveOrganisation(orgId: string): Promise<unknown> {
    return this.request("DELETE", `/office/organisations/${encodeURIComponent(orgId)}/membership`);
  }

  /** The industries a BUSINESS trades in. NULL for every other kind (A15). */
  businessCategories(): Promise<{ categories: string[] }> {
    return this.request("GET", "/business/categories");
  }

  statusViewers(statusId: string): Promise<{ viewers: StatusViewerRow[] }> {
    return this.request("GET", `/statuses/${encodeURIComponent(statusId)}/viewers`);
  }

  viewStatus(statusId: string): Promise<unknown> {
    return this.request("POST", `/statuses/${encodeURIComponent(statusId)}/view`);
  }

  likeStatus(statusId: string, liked: boolean): Promise<unknown> {
    const path = `/statuses/${encodeURIComponent(statusId)}/like`;
    return this.request(liked ? "POST" : "DELETE", path);
  }

  deleteStatus(statusId: string): Promise<unknown> {
    return this.request("DELETE", `/statuses/${encodeURIComponent(statusId)}`);
  }

  muteStatusAuthor(authorId: string, muted: boolean): Promise<unknown> {
    const path = `/statuses/mutes/${encodeURIComponent(authorId)}`;
    return this.request(muted ? "PUT" : "DELETE", path);
  }

  /**
   * Report a user, naming the status when the report came from the viewer.
   *
   * `status_id` is what lets the Ops queue offer a take-down rather than only
   * a suspension: the status is gone in 24 hours, so a report that names only
   * the account leaves a moderator with nothing to look at.
   */
  reportUser(
    userId: string,
    reason: string,
    opts: { statusId?: string; chatId?: string; details?: string } = {},
  ): Promise<unknown> {
    return this.request("POST", "/reports", {
      body: {
        user_id: userId,
        reason,
        ...(opts.statusId ? { status_id: opts.statusId } : {}),
        ...(opts.chatId ? { chat_id: opts.chatId } : {}),
        ...(opts.details ? { details: opts.details } : {}),
      },
    });
  }

  /** One surface's privacy settings — avatar, status, last_seen or
   *  findability (ADR-0031 D3). The response names the policies and lists
   *  that surface offers, so a caller renders what exists. */
  privacy(surface: string): Promise<PrivacyAudience> {
    return this.request("GET", `/privacy/${encodeURIComponent(surface)}`);
  }

  setPrivacy(
    surface: string,
    policy: string,
    lists: { except?: string[]; only?: string[] } = {},
  ): Promise<PrivacyAudience> {
    // A list left ABSENT means "leave it alone"; present means "replace it".
    // The two are different answers, so the body carries only what changed.
    return this.request("PUT", `/privacy/${encodeURIComponent(surface)}`, {
      body: { policy, ...lists },
    });
  }

  avatarHistory(): Promise<{ history: { version: string; set_at: string | null; removed_at: string | null; current: boolean }[] }> {
    return this.request("GET", "/auth/profile/avatar-history");
  }

  restoreAvatar(version: string): Promise<{ user: User }> {
    return this.request("POST", `/auth/profile/avatar-history/${version}/restore`);
  }

  forgetAvatar(version: string): Promise<{ history: { version: string; set_at: string | null; removed_at: string | null; current: boolean }[] }> {
    return this.request("DELETE", `/auth/profile/avatar-history/${version}`);
  }

  profile(): Promise<{ user: User }> {
    return this.request<{ user: User }>("GET", "/auth/profile");
  }

  /** E5 / B9 — one user by id, what a scanned QR and a tapped card resolve.
   *  Rejects with a 404 for nobody, for an inactive account and (once
   *  ADR-0031 D3 lands) for one that has chosen not to be found — the same
   *  answer to all three, deliberately. */
  getUser(userId: string): Promise<{ user: { id: string; username: string | null; display_name: string | null; reachability?: string | null; avatar?: unknown; frame?: string | null } }> {
    return this.request("GET", `/users/${encodeURIComponent(userId)}`);
  }

  /** E5 — my own contact QR. The SERVER renders the SVG here (unlike the
   *  phones, which draw their own) because a browser is online by definition
   *  — ADR-0032 D3. */
  myContactQr(): Promise<{ payload: string; qr_svg?: string | null }> {
    return this.request("GET", "/users/me/contact-qr");
  }

  /** B9 — a one-to-one conversation with somebody a card named. */
  createDirectChat(peerUserId: string): Promise<Chat> {
    return this.request("POST", "/chats", {
      body: { chat_type: "direct", member_ids: [peerUserId] },
    });
  }

  // ADR-0030 D4 — `reachability` is "ok" | "quiet" | "unused"; absent means ok.
  searchUsers(query: string): Promise<{ users: { id: string; username: string | null; display_name: string | null; reachability?: string | null; avatar?: unknown; frame?: string | null }[] }> {
    return this.request("GET", `/users/search?q=${encodeURIComponent(query)}`);
  }

  private async request<T>(
    method: string,
    path: string,
    opts: { body?: unknown; auth?: boolean } = {},
  ): Promise<T> {
    const useAuth = opts.auth !== false;
    if (useAuth && !this.token) throw new ApiError(401, "not authenticated");

    const sentWith = this.token;
    const first = await this.send(method, path, opts, useAuth);
    if (first.status !== 401 || !useAuth) return this.settle<T>(first);

    // The access token was refused. Before deciding that the SESSION is over,
    // find out — a browser tab left open overnight has an expired access token
    // and a perfectly good refresh token, and that is the ordinary case, not an
    // edge one. See `rotate()` for why the three outcomes are not two.
    const outcome = await this.rotate(sentWith);
    if (outcome === "refreshed") {
      const retry = await this.send(method, path, opts, useAuth);
      if (retry.status !== 401) return this.settle<T>(retry);
      // A token minted a moment ago, refused on its first use. That is the
      // server saying the session is over in the one way that leaves no room
      // for a kinder reading.
      this.onSessionEnded?.();
      return this.settle<T>(retry);
    }
    if (outcome === "rejected") {
      this.onSessionEnded?.();
      return this.settle<T>(first);
    }
    // We could not find out. The session stays; this one request fails as the
    // transient thing it is, and the next attempt may well succeed.
    throw new ApiError(0, "session could not be refreshed — please try again");
  }

  /** Issue one attempt and hand back what came off the wire. */
  private async send(
    method: string,
    path: string,
    opts: { body?: unknown; auth?: boolean },
    useAuth: boolean,
  ): Promise<{ status: number; ok: boolean; payload: unknown }> {
    const headers: Record<string, string> = {};
    // ADR-0033 D9 — say which build this is on EVERY request, authenticated or
    // not. A floor applies to a login and a token refresh exactly as much as
    // to a chat fetch: a gate that only saw authenticated traffic would let an
    // old client sign in and then start refusing it, which is the worst of
    // both answers.
    if (this.clientBuild) {
      headers["x-client-platform"] = this.clientBuild.platform;
      headers["x-client-build"] = String(this.clientBuild.build);
    }
    if (useAuth) {
      if (!this.token) throw new ApiError(401, "not authenticated");
      headers["authorization"] = `Bearer ${this.token}`;
    }
    let body: string | undefined;
    if (opts.body !== undefined) {
      headers["content-type"] = "application/json";
      body = JSON.stringify(opts.body);
    }

    let res: Awaited<ReturnType<FetchLike>>;
    try {
      res = await this.doFetch(`${this.baseUrl}/api/v1${path}`, { method, headers, body });
    } catch (e) {
      throw new ApiError(0, `network error: ${(e as Error).message}`);
    }

    return { status: res.status, ok: res.ok, payload: await safeJson(res) };
  }

  /** Turn an attempt into a value or the right ApiError. */
  private settle<T>(r: { status: number; ok: boolean; payload: unknown }): T {
    const payload = r.payload;
    // ADR-0033 D9. Raised before the generic error below, and never retried:
    // this refusal stands for as long as this bundle is the one running, so a
    // retry is a loop against a wall.
    if (r.status === 426) {
      this.onUpdateRequired?.();
      throw new ApiError(426, "update_required", payload);
    }
    if (!r.ok) {
      const msg =
        (payload && typeof payload === "object" && "error" in payload
          ? String((payload as { error: unknown }).error)
          : `request failed (${r.status})`);
      throw new ApiError(r.status, msg, payload);
    }
    return payload as T;
  }
}

// Map a registry App object (GET /miniapps/featured|installed) to a picker row.
function toListItem(app: Record<string, unknown>): MiniAppListItem {
  const t = hostTranslator();
  return {
    id: String(app.id ?? app.app_id ?? ""),
    app_id: String(app.app_id ?? app.id ?? ""),
    name: String(app.name ?? t("Mini-app")),
    description: (app.description as string | null) ?? null,
    icon_url: (app.icon_url as string | null) ?? null,
  };
}

async function safeJson(res: {
  json: () => Promise<unknown>;
  text: () => Promise<string>;
}): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
